import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Box, Chip, MenuItem, Skeleton, TextField, Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { fetchDayBook } from '../api/endpoints'
import { fmtMoney } from '../theme'
import ReportShell, { rangeLabel } from './ReportShell'
import ReportFooter from './ReportFooter'
import { gregorian } from './hijri'
import ReportToolbar from './ReportToolbar'
import VoucherDrawer from './VoucherDrawer'
import { useDateRange } from './useDateRange'
import { downloadCsv } from './csv'

const KINDS = ['', 'CONTRA', 'PAYMENT', 'RECEIPT', 'JOURNAL', 'SALES',
               'PURCHASE', 'REVERSAL']

export default function DayBook({ onLedger }: { onLedger?: (c: string) => void }) {
  const { t, i18n } = useTranslation()
  const { range, setRange, presets } = useDateRange()
  const [kind, setKind] = useState('')
  const [open, setOpen] = useState<number | null>(null)

  const q = useQuery({
    queryKey: ['daybook', range.from, range.to, kind],
    queryFn: () => fetchDayBook({
      from_date: range.from, to_date: range.to, kind: kind || undefined }),
  })

  const total = useMemo(
    () => q.data?.entries.reduce((a, e) => a + Number(e.amount), 0) ?? 0,
    [q.data])

  return (
    <>
      <ReportShell titleKey="nav.dayBook"
        period={rangeLabel(range.from, range.to, i18n.language, t)}
        signatures={false}
        headRight={q.data ? (
          <Chip size="small" variant="outlined" label={q.data.count} />
        ) : undefined}
        toolbar={
          <ReportToolbar range={range} setRange={setRange} presets={presets}
          pdf={{ path: 'daybook.pdf',
                 params: { from_date: range.from, to_date: range.to,
                           kind: kind || undefined } }}
            onExport={() => downloadCsv(`daybook-${range.from}-${range.to}`, [
              ['Date', 'Voucher', 'Type', 'Narration', 'Amount', 'Status'],
              ...(q.data?.entries ?? []).map((e) => [
                e.date, e.voucher_no, e.voucher_kind, e.narration ?? '',
                e.amount, e.status]),
            ])}>
            <TextField select size="small" label={t('report.type')} value={kind}
              onChange={(e) => setKind(e.target.value)} sx={{ width: 150 }}>
              {KINDS.map((k) => (
                <MenuItem key={k} value={k}>{k || t('report.allTypes')}</MenuItem>
              ))}
            </TextField>
          </ReportToolbar>
        }>
        {q.isLoading ? (
          <Box sx={{ p: 2 }}><Skeleton /><Skeleton /><Skeleton /></Box>
        ) : !q.data?.entries.length ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
            {t('report.nothingInRange')}
          </Typography>
        ) : (
          <Box sx={{ px: 2, pb: 1 }}>
            <table className="rpt">
              <thead>
                <tr>
                  <th style={{ width: 104 }}>{t('entry.date')}</th>
                  <th style={{ width: 150 }}>{t('report.voucherNo')}</th>
                  <th style={{ width: 100 }}>{t('report.type')}</th>
                  <th>{t('entry.narration')}</th>
                  <th className="num" style={{ width: 130 }}>{t('report.amount')}</th>
                </tr>
              </thead>
              <tbody>
                {q.data.entries.map((e) => (
                  <tr key={e.voucher_id} className="clickable"
                    onClick={() => setOpen(e.voucher_id)}
                    style={{ opacity: e.status === 'REVERSED' ? 0.55 : 1 }}>
                    <td className="num" style={{ textAlign: 'start' }}>
                      {gregorian(e.date, i18n.language)}</td>
                    <td className="num" style={{ textAlign: 'start',
                      color: 'var(--ink-muted)', fontSize: '0.78rem' }}>
                      {e.voucher_no}</td>
                    <td style={{ fontSize: '0.78rem', color: 'var(--ink-muted)' }}>
                      {e.voucher_kind}</td>
                    <td>{e.narration ?? '—'}</td>
                    <td className="num" style={{ textDecoration:
                      e.status === 'REVERSED' ? 'line-through' : 'none' }}>
                      {fmtMoney(e.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="total-row">
                  <td colSpan={4}>{t('report.total')}</td>
                  <td className="num">{fmtMoney(total)}</td>
                </tr>
              </tfoot>
            </table>
          </Box>
        )}
        <ReportFooter />
      </ReportShell>

      <VoucherDrawer voucherId={open} onClose={() => setOpen(null)}
        onLedgerClick={onLedger} />
    </>
  )
}
